import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../../services/api'
import { useAccount } from '../../../hooks/useAccount'

function parseReviews(topReviews) {
  if (typeof topReviews === 'string') {
    try { return JSON.parse(topReviews) } catch { return [] }
  }
  return Array.isArray(topReviews) ? topReviews : []
}

function EcommerceCreateStoriesPage() {
  const { t } = useTranslation('ecommerce')
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedAccount } = useAccount()

  const [products, setProducts] = useState([])
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState({ current: 0, total: 0 })

  // Get productIds from navigation state
  const productIds = location.state?.productIds || []

  // Redirect if no products selected
  useEffect(() => {
    if (productIds.length === 0) {
      navigate('/ecommerce/products')
    }
  }, [productIds, navigate])

  // Fetch products and templates
  const fetchData = useCallback(async () => {
    if (!selectedAccount?.id || productIds.length === 0) return

    try {
      setLoading(true)
      setError(null)

      const [productsResult, templatesResult] = await Promise.all([
        api.getProducts({ instagram_account_id: selectedAccount.id }),
        api.getVideoTemplates().catch(() => ({ templates: [] })),
      ])

      // Filter to only selected products
      const selectedProducts = (productsResult.products || []).filter(p =>
        productIds.includes(p.product_id)
      )
      setProducts(selectedProducts)
      setTemplates(templatesResult.templates || [])

      // Auto-select first non-SocialProof template (it may be disabled)
      if (templatesResult.templates?.length > 0) {
        const firstUsable = templatesResult.templates.find(
          t => t.template_id !== 'SocialProofShowcase'
        ) || templatesResult.templates[0]
        setSelectedTemplate(firstUsable.template_id)
      }
    } catch (err) {
      console.error('Failed to fetch data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [selectedAccount?.id, productIds])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleCreateStories = useCallback(async () => {
    if (!selectedTemplate || products.length === 0) return

    try {
      setCreating(true)
      setError(null)
      setProgress({ current: 0, total: products.length })

      // Create stories for each product
      for (let i = 0; i < products.length; i++) {
        const product = products[i]
        setProgress({ current: i + 1, total: products.length })

        try {
          await api.createProductProposal(product.product_id, {
            template_id: selectedTemplate,
          })
        } catch (err) {
          console.error(`Failed to create story for ${product.title}:`, err)
          // Continue with other products
        }
      }

      // Navigate to approval page after all stories are created
      navigate('/approval', {
        state: { message: t('createStories.success', 'Stories werden erstellt und erscheinen bald zur Freigabe.') }
      })
    } catch (err) {
      console.error('Failed to create stories:', err)
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }, [selectedTemplate, products, navigate, t])

  const allProductsHaveReviews = products.every(
    p => parseReviews(p.top_reviews).length > 0
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-600">
          {t('common.loading', 'Loading...')}
        </span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('createStories.title', 'Create Stories')}
          </h1>
          <p className="mt-1 text-gray-600">
            {t('createStories.subtitle', 'Create Instagram stories from your products')}
          </p>
        </div>
        <button
          onClick={() => navigate('/ecommerce/products')}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          {t('common.cancel', 'Cancel')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Selected Products */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('createStories.selectedProducts', 'Selected Products')} ({products.length})
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {products.map(product => {
            const reviews = parseReviews(product.top_reviews)
            const hasReviews = reviews.length > 0

            return (
              <div key={product.product_id} className="text-center relative">
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                />
                <span className={`inline-flex items-center mt-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  hasReviews
                    ? 'bg-green-100 text-green-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {hasReviews
                    ? `${reviews.length} Reviews`
                    : t('createStories.noReviews', 'No reviews')
                  }
                </span>
                <p className="mt-1 text-xs text-gray-600 truncate">{product.title}</p>
                <p className="text-sm font-medium text-gray-900">
                  {product.currency} {product.price}
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Template Selection */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('createStories.selectTemplate', 'Select Template')}
        </h2>
        {templates.length === 0 ? (
          <p className="text-gray-500">
            {t('createStories.noTemplates', 'No templates available')}
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {templates.map(template => {
              const isSocialProof = template.template_id === 'SocialProofShowcase'
              const isDisabled = isSocialProof && !allProductsHaveReviews
              const isSelected = selectedTemplate === template.template_id

              let borderClass = 'border-gray-200 hover:border-gray-300'
              if (isDisabled) borderClass = 'border-gray-200 opacity-50 cursor-not-allowed'
              else if (isSelected) borderClass = 'border-primary-500 bg-primary-50'

              return (
                <button
                  key={template.template_id}
                  onClick={() => setSelectedTemplate(template.template_id)}
                  disabled={isDisabled}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${borderClass}`}
                >
                  <h3 className="font-medium text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                  {isDisabled && (
                    <p className="text-xs text-red-500 mt-1 italic">
                      {t('createStories.needsReviews', 'Requires products with customer reviews')}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    {template.duration}s &bull; {template.layer_count} layers
                  </p>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-6">
        <div className="text-sm text-gray-600">
          {creating && (
            <span>
              {t('createStories.progress', 'Creating story')} {progress.current} / {progress.total}...
            </span>
          )}
        </div>
        <button
          onClick={handleCreateStories}
          disabled={creating || !selectedTemplate || products.length === 0}
          className="px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {creating ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('createStories.creating', 'Creating...')}
            </span>
          ) : (
            t('createStories.create', 'Create {count} Stories').replace('{count}', products.length)
          )}
        </button>
      </div>
    </div>
  )
}

export default EcommerceCreateStoriesPage
